import { zodResolver } from '@hookform/resolvers/zod';
import { Resolver, UseFormProps } from 'react-hook-form';
import { z } from 'zod';

import { createTranslationSchema } from '../../../../utils/getTranslationSchema';

export const createTranslationSchemaOptional = (
  errMsg: string,
  maxLen = 2000,
) => z.intersection(
  z.object({
    pl: z.string()
      .max(maxLen, `Maksymalna liczba znaków to ${maxLen}`).optional(),
  }).optional(),
  z.record(z.string(), z.string()).optional(),
);

const AttractionType = z.enum(['FORKIDS', 'TRIP', 'CULTURE', 'FOODS'], {
  required_error: 'Attraction category is required.',
  invalid_type_error: 'Invalid attraction category.',
});

const StatusType = z.enum(['PREVIEW', 'LIVE'], {
  required_error: 'Status is required.',
  invalid_type_error: 'Invalid status.',
});

const LocalizationSchema = z.object({
  latitude: z
    .number({
      required_error: 'Szerokość geograficzna jest wymagana',
      invalid_type_error: 'Szerokość geograficzna musi być liczbą',
    })
    .min(-90, 'Szerokość geograficzna musi być większa lub równa -90')
    .max(90, 'Szerokość geograficzna musi być mniejsza lub równa 90')
    .refine((val) => !Number.isNaN(val), {
      message: 'Szerokość geograficzna musi być prawidłową liczbą',
    }),
  longitude: z
    .number({
      required_error: 'Długość geograficzna jest wymagana',
      invalid_type_error: 'Długość geograficzna musi być liczbą',
    })
    .min(-180, 'Długość geograficzna musi być większa lub równa -180')
    .max(180, 'Długość geograficzna musi być mniejsza lub równa 180')
    .refine((val) => !Number.isNaN(val), {
      message: 'Długość geograficzna musi być prawidłową liczbą',
    }),
});

const DiscountSchema = z.object({
  title: createTranslationSchemaOptional('Title is required.'),
  description: createTranslationSchemaOptional('Description is required'),
  discount_code: z.string().optional(),
  end_date: z.any().optional(),
  value: z.number({ invalid_type_error: 'Value must be a number' }).nullable(),
});

const addressSchema = z.object({
  city: z.string().min(1, { message: 'Required City' }),
  street: z.string().optional(),
  number: z.string().optional(),
  postal_code: z.string().optional(),
  geoPoint: LocalizationSchema,
});

export const schemaAttraction = z
  .object({
    title: createTranslationSchema('Title is required.'),
    description: createTranslationSchema('Description is required.'),
    address: addressSchema,
    cover_photo: z.string().min(1, 'Cover photo is required.'),
    gallery: z.array(z.string()).max(9, 'Max 9 images in gallery.').optional(),
    type: AttractionType,
    web_url: z.string().optional(),
    isBuyTicketUrl: z.boolean().optional(),
    recomended: z.boolean(),
    languages: z.array(z.string()),
    brand_icon: z.string().optional(),
    showDiscount: z.boolean(),
    discount: DiscountSchema.optional(),
    createdBy: z.string().min(1, 'Crated by is required.'),
    status: StatusType,
    email: z.string().email('Invalid email format').optional().or(z.literal('')),
    phone: z.string().optional(),
    endDate: z.string().optional(),
    reportEmail: z.string().email('Invalid email format').optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    // Walidacja współrzędnych geograficznych
    const { latitude, longitude } = data.address.geoPoint;

    if (latitude === 0 && longitude === 0) {
      ctx.addIssue({
        path: ['address', 'geoPoint'],
        code: z.ZodIssueCode.custom,
        message: 'Współrzędne geograficzne są wymagane. Użyj przycisku "Generuj współrzędne" lub wprowadź je ręcznie.',
      });
    }

    // Sprawdzenie czy współrzędne są w prawidłowym zakresie (dodatkowe zabezpieczenie)
    if (latitude < -90 || latitude > 90) {
      ctx.addIssue({
        path: ['address', 'geoPoint', 'latitude'],
        code: z.ZodIssueCode.custom,
        message: 'Szerokość geograficzna musi być w zakresie od -90 do 90',
      });
    }

    if (longitude < -180 || longitude > 180) {
      ctx.addIssue({
        path: ['address', 'geoPoint', 'longitude'],
        code: z.ZodIssueCode.custom,
        message: 'Długość geograficzna musi być w zakresie od -180 do 180',
      });
    }

    // Walidacja discount
    if (data.showDiscount === true) {
      if (!data.discount) {
        ctx.addIssue({
          path: ['discount'],
          code: z.ZodIssueCode.custom,
          message: 'Discount is required when showDiscount is true.',
        });
        return;
      }

      // eslint-disable-next-line camelcase
      const { value, discount_code, title } = data.discount;

      if (value === null || value === undefined) {
        ctx.addIssue({
          path: ['discount', 'value'],
          code: z.ZodIssueCode.custom,
          message: 'Discount value is required.',
        });
      }
      // eslint-disable-next-line camelcase
      if (!discount_code) {
        ctx.addIssue({
          path: ['discount', 'discount_code'],
          code: z.ZodIssueCode.custom,
          message: 'Discount code is required.',
        });
      }

      if (!title?.pl) {
        ctx.addIssue({
          path: ['discount', 'title', 'pl'],
          code: z.ZodIssueCode.custom,
          message: 'Discount title (PL) is required.',
        });
      }
    }
  });

export type AttractionFormEntity = z.infer<typeof schemaAttraction>;

const attractionResolver: Resolver<AttractionFormEntity> = zodResolver(schemaAttraction) as never;

export const AttractionFormOptions: UseFormProps<AttractionFormEntity> = {
  mode: 'onSubmit',
  resolver: attractionResolver,
};
